import { API } from "@/main.js";

const getDefaultState = () => {
  return {
    loading: false,
    workspaces: undefined,
    selectedWorkspaceUuid: undefined,
    selectedTlUuid: undefined,
    selectedClUuid: undefined,
    diagramData: undefined,
    selectedObject: {
      type: undefined,
      nodeType: undefined,
      objectData: undefined
    },
    objectMenu: {
      open: false,
      coordinates: {
        xPosition: undefined,
        yPosition: undefined
      }
    },
    zoomIn: {
      open: false,
      update: undefined
    },
    qualityControl: undefined,
    scrollPosition: {
      x: 0,
      y: 0
    }
  };
};

const state = getDefaultState();

const getters = {
  getNetworkDiagramTechnicalLocalUUID: state => state.selectedTlUuid,
  getNetworkDiagramClosureUUID: state => state.selectedClUuid,
  getNetworkDiagramWorkspaceUUID: state => state.selectedWorkspaceUuid,
  isNetworkDiagramStartingPointSelected: state =>
    state.selectedTlUuid !== undefined,
  getNetworkDiagramObjectMenuCoordinates: state => state.objectMenu.coordinates,
  getNetworkDiagramSelectedObjectInfo: state => state.selectedObject,
  getNetworkDiagramData: state => state.diagramData,
  isNetworkDiagramLoading: state => state.loading,

  isNetworkDiagramZoomInOpen: state => state.zoomIn.open,
  isNetworkDiagramObjectMenuOpen: state => state.objectMenu.open,

  getScrollPosition: state => state.scrollPosition,
  getQualityControl: state => state.qualityControl
};

const mutations = {
  RESET_NETWORK_DIAGRAM_STATE(state) {
    Object.assign(state, getDefaultState());
  },

  SET_NETWORK_DIAGRAM_DATA(state, data) {
    state.diagramData = data;
  },

  SET_NETWORK_DIAGRAM_TECHNICAL_LOCAL_UUID(state, uuid) {
    state.selectedTlUuid = uuid;
  },

  SET_NETWORK_DIAGRAM_CLOSURE_UUID(state, uuid) {
    state.selectedClUuid = uuid;
  },

  SET_NETWORK_DIAGRAM_WORKSPACE_UUID(state, uuid) {
    state.selectedWorkspaceUuid = uuid;
  },

  TOGGLE_NETWORK_DIAGRAM_LOADING(state, loading) {
    state.loading = loading;
  },

  TOGGLE_NETWORK_DIAGRAM_OBJECT_MENU(state, value) {
    state.objectMenu.open = value;
  },

  SET_NETWORK_DIAGRAM_OBJECT_MENU_COORDINATES(state, data) {
    state.objectMenu.coordinates.xPosition = data.x;
    state.objectMenu.coordinates.yPosition = data.y;
  },

  SET_NETWORK_DIAGRAM_SELECTED_OBJECT_DATA(state, data) {
    state.selectedObject.type = data.type;
    state.selectedObject.nodeType = data.nodeType;
    state.selectedObject.objectData = data.objectData;
  },

  TOGGLE_NETWORK_DIAGRAM_ZOOM_IN(state, value) {
    state.zoomIn.open = value;
    state.zoomIn.update = Math.random();
  },

  RESET_NETWORK_DIAGRAM_SELECTED_OBJECT_DATA(state) {
    state.selectedObject = {
      type: undefined,
      nodeType: undefined,
      objectData: undefined
    };
  },

  SET_SCROLL_POSITION(state, scrollPosition) {
    state.scrollPosition = scrollPosition;
  },

  SET_QUALITY_CONTROL(state, qualityControl) {
    state.qualityControl = qualityControl;
  }
};

const actions = {
  RESET_NETWORK_DIAGRAM_STATE({ commit }) {
    commit("RESET_NETWORK_DIAGRAM_STATE");
  },

  SET_NETWORK_DIAGRAM_TECHNICAL_LOCAL_UUID({ commit }, uuid) {
    commit("SET_NETWORK_DIAGRAM_TECHNICAL_LOCAL_UUID", uuid);
  },

  SET_NETWORK_DIAGRAM_CLOSURE_UUID({ commit }, uuid) {
    commit("SET_NETWORK_DIAGRAM_CLOSURE_UUID", uuid);
  },

  SET_NETWORK_DIAGRAM_WORKSPACE_UUID({ commit }, uuid) {
    commit("SET_NETWORK_DIAGRAM_WORKSPACE_UUID", uuid);
  },

  SET_QUALITY_CONTROL_RESULTS({ commit }, results) {
    commit("SET_QUALITY_CONTROL", results);
  },

  GET_NETWORK_DIAGRAM_DATA({ commit }, { nodeUUID, workspaceUUID }) {
    commit("TOGGLE_NETWORK_DIAGRAM_LOADING", true);
    return new Promise((resolve, reject) => {
      API.workspace_design.getByUUID(
        nodeUUID,
        "technical_local",
        workspaceUUID,
        result => {
          let diagramData = result.data.data[0];
          if (diagramData) {
            commit("SET_NETWORK_DIAGRAM_DATA", diagramData);
          }
          commit("TOGGLE_NETWORK_DIAGRAM_LOADING", false);
          resolve(result);
        },
        error => {
          reject(error);
          commit("TOGGLE_NETWORK_DIAGRAM_LOADING", false);
        }
      );
    });
  },

  GET_NETWORK_DIAGRAM_DATA_FROM_CLOSURE({ commit }, { nodeUUID, workspaceUUID }) {
    commit("TOGGLE_NETWORK_DIAGRAM_LOADING", true);
    return new Promise((resolve, reject) => {
      API.workspace_design.getByUUID(
        nodeUUID,
        "closure",
        workspaceUUID,
        result => {
          let diagramData = result.data.data[0];
          if (diagramData) {
            commit("SET_NETWORK_DIAGRAM_DATA", diagramData);
          }
          commit("TOGGLE_NETWORK_DIAGRAM_LOADING", false);
          resolve(result);
        },
        error => {
          reject(error);
          commit("TOGGLE_NETWORK_DIAGRAM_LOADING", false);
        }
      );
    });
  },

  GET_WORKSPACE_TECHNICAL_SITES_WITH_LOCALS({ commit }, workspaceUuid) {
    return new Promise((resolve, reject) => {
      API.networkObject.getTechnicalSitesWithTechnicalLocals(
        workspaceUuid,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  GET_WORKSPACE_CLOSURES({ commit }, workspaceUuid) {
    return new Promise((resolve, reject) => {
      API.networkObject.getAllClosures(
        workspaceUuid,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  GET_WORKSPACE_TECHNICAL_LOCAL_CLOSURES({ commit },closureData) {
    return new Promise((resolve, reject) => {
      API.networkObject.getClosuresByTechnicalLocal(
        closureData,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  RESET_NETWORK_DIAGRAM_TECHNICAL_LOCAL_UUID({ commit }) {
    commit("SET_NETWORK_DIAGRAM_TECHNICAL_LOCAL_UUID", undefined);
    commit("SET_NETWORK_DIAGRAM_DATA", undefined);
  },

  OPEN_NETWORK_DIAGRAM_OBJECT_MENU({ commit, dispatch }, data) {
    dispatch(
      "SET_NETWORK_DIAGRAM_OBJECT_MENU_COORDINATES",
      data.positioningInfo
    );
    commit("SET_NETWORK_DIAGRAM_SELECTED_OBJECT_DATA", data.selectedObject);
    commit("TOGGLE_NETWORK_DIAGRAM_OBJECT_MENU", true);
  },

  CLOSE_NETWORK_DIAGRAM_OBJECT_MENU({ commit }) {
    commit("TOGGLE_NETWORK_DIAGRAM_OBJECT_MENU", false);
  },

  SET_NETWORK_DIAGRAM_OBJECT_MENU_COORDINATES({ commit }, data) {
    if (data !== undefined) {
      commit("SET_NETWORK_DIAGRAM_OBJECT_MENU_COORDINATES", {
        x: data.x,
        y: data.y
      });
    }
  },
  TOGGLE_NETWORK_DIAGRAM_ZOOM_IN({ commit }, value) {
    commit("TOGGLE_NETWORK_DIAGRAM_ZOOM_IN", value);
  },

  GET_TECHNICAL_LOCAL_WITH_LOGICAL_RELATIONS({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.networkObject.getTechnicalLocalWithLogicalRelations(
        data.technicalLocalUUID,
        data.workspaceUUID,
        data.includes,
        result => {
          resolve(result.data.data[0]);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  getNetworkDiagramAsCADFile({ commit }, networkData) {
    return new Promise((resolve, reject) => {
      API.workspace_design.exportAsCAD(
        networkData,
        result => {
          resolve(result.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  setScrollPosition({ commit }, data) {
    commit("SET_SCROLL_POSITION", data);
  }
};

export default { state, getters, actions, mutations };



// WEBPACK FOOTER //
// ./src/store/modules/Home/networkDiagram.js